/* eslint-disable max-len */
/* eslint-disable react/jsx-one-expression-per-line */
import LoadingPage from "@/components/Layouts/LoadingPage";
import { useQuery } from "@tanstack/react-query";
import {
  Button,
  Card,
  Col,
  Divider,
  Layout,
  message,
  Row,
  Typography,
} from "antd";
import { useSession } from "next-auth/react";
import Head from "next/head";
import Router from "next/router";
import { useEffect, useState } from "react";
import LayoutBase from "../components/Layouts/LayoutBase";
import useSignOut from "../lib/hooks/useSignOut";
import { errorToString, graphqlClient } from "../lib/util";
import { graphql } from "../__generated__/gql";

const indexQueryDocument = graphql(`
  query whoamiIndex {
    whoami {
      id
      firstName
      lastName
      emailAddress
      tenantUserRelationships {
        id
        tenantId
        role
        tenant {
          id
          name
          shortId
        }
      }
    }
  }
`);

// A single transient session-read hiccup must not immediately bounce an
// already-authenticated multi-org user (staging, demo) to sign-in. Give the
// session a bounded, two-step grace window before treating "unauthenticated"
// as final: (1) after a short delay, fire a background session refetch; (2)
// after another short delay, redirect ONLY if the status is still
// "unauthenticated" at that point. Splitting the two delays (rather than
// deciding right when the refetch is fired) matters: `session.update()`
// resolving is what flips `session.status` via next-auth's own context, and
// that update lands on a LATER render — checking in the same tick would only
// ever see the stale pre-retry status. See pages/api/auth/[...nextauth].ts.
export const SESSION_RETRY_DELAY_MS = 800;
export const SESSION_CONFIRM_DELAY_MS = 800;

export default function Home() {
  const session = useSession();
  const [retryPhase, setRetryPhase] = useState<"idle" | "confirming">("idle");

  useEffect(() => {
    if (session.status !== "unauthenticated") {
      if (retryPhase !== "idle") setRetryPhase("idle");
      return undefined;
    }
    if (retryPhase === "idle") {
      const timer = setTimeout(() => {
        session.update();
        setRetryPhase("confirming");
      }, SESSION_RETRY_DELAY_MS);
      return () => clearTimeout(timer);
    }
    const timer = setTimeout(() => {
      const url = `/api/auth/signin?${new URLSearchParams({
        error: "SessionRequired",
        callbackUrl: window.location.href,
      })}`;
      window.location.href = url;
    }, SESSION_CONFIRM_DELAY_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.status, retryPhase]);

  const userQuery = useQuery({
    queryKey: ["indexWhoami", session.data?.user?.email],
    queryFn: async () => graphqlClient.request(indexQueryDocument),
    enabled: session.status === "authenticated",
    onError: (e) => message.error(errorToString(e)),
  });
  const user = userQuery.data?.whoami;
  const handleSignOut = useSignOut();

  if (user?.tenantUserRelationships.length === 1) {
    Router.replace(`/tenant/${user.tenantUserRelationships[0].tenant.id}`);
    return <LoadingPage />;
  }

  if (session.status === "loading") return <LoadingPage />;
  if (session.status === "unauthenticated") return <LoadingPage />;
  if (session.status === "authenticated" && (userQuery.isLoading || userQuery.isFetching)) return <LoadingPage />;

  if (!user) {
    return (
      <div>
        <Head>
          <meta charSet="UTF-8" />
          <title>Sign In</title>
          <meta
            name="viewport"
            content="width=device-width, initial-scale=1, maximum-scale=1.0, user-scalable=no"
          />
        </Head>
        <LayoutBase title="No User">
          <Layout.Content
            style={{ backgroundColor: "#f5f5f5", minHeight: "100vh" }}
          >
            <Row style={{ padding: 24 }} justify="center" align="middle">
              <Col flex="368px">
                <Card
                  style={{ marginBottom: 16 }}
                  title="No User Found"
                  // extra={(
                  // )}
                >
                  <Typography.Paragraph>
                    We could not find an associated user and you are not
                    correctly signed in.
                  </Typography.Paragraph>
                  <Typography.Paragraph>
                    Please attempt to sign in again.
                  </Typography.Paragraph>
                  <Button
                    onClick={() => handleSignOut()}
                    block
                    size="large"
                    type="primary"
                    // style={{ marginBottom: 24 }}
                  >
                    Sign In
                  </Button>
                </Card>
              </Col>
            </Row>
          </Layout.Content>
        </LayoutBase>
      </div>
    );
  }

  return (
    <div>
      <Head>
        <meta charSet="UTF-8" />
        <title>Sign In</title>
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, maximum-scale=1.0, user-scalable=no"
        />
      </Head>
      <LayoutBase title="Select an Organization">
        <Layout.Content
          style={{ backgroundColor: "#f5f5f5", minHeight: "100vh" }}
        >
          <Row style={{ padding: 24 }} justify="center" align="middle">
            <Col flex="368px">
              <Divider>Select an Organization</Divider>
              {user.tenantUserRelationships
                .filter((rel) => rel.role !== "client")
                .map((relationship) => (
                  <Card
                    key={relationship.tenant.id}
                    style={{ marginBottom: 16 }}
                    title={relationship.tenant.name}
                    extra={
                      <Button
                        type="primary"
                        onClick={() => {
                          // setTenantId(relationship.tenant.id);
                          Router.push(`/tenant/${relationship.tenant.id}`);
                        }}
                      >
                        Select
                      </Button>
                    }
                  >
                    You are a{" "}
                    <Typography.Text mark strong>
                      {relationship.role}
                    </Typography.Text>
                  </Card>
                ))}
              <Button
                onClick={() => handleSignOut()}
                block
                style={{ marginBottom: 24 }}
              >
                Sign Out
              </Button>
            </Col>
          </Row>
        </Layout.Content>
      </LayoutBase>
    </div>
  );
}
